import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Lock, Mail, Send, UserPlus, Crown, 
  Clock, Trash2, ArrowLeft, Settings, Plus, X,
  CheckCircle, AlertCircle, RefreshCw, ArrowRightLeft, Calendar, Edit,
  Link2, Copy, Share2, Upload, BarChart3, Bell
} from 'lucide-react';
import PollCard from '@/components/PollCard';
import CreatePollDialog from '@/components/CreatePollDialog';

const PRESET_CATEGORIES = [
  'Sports & Fitness',
  'Food & Drinks',
  'Arts & Entertainment',
  'Travel & Adventure',
  'Social & Lifestyle',
  'Professional',
  'Family & Kids',
];

const GroupDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersCountryInfo, setMembersCountryInfo] = useState({ country: '', total_all: 0 });
  const [pendingInvites, setPendingInvites] = useState([]);
  const [groupEvents, setGroupEvents] = useState([]);
  const [showPastGroupEvents, setShowPastGroupEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Partition group events into upcoming vs past based on the event date.
  // Past = date strictly before today's YYYY-MM-DD (viewer-local); this
  // matches how the main /events feed rolls events off after their date.
  const { upcomingGroupEvents, pastGroupEvents } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    const upcoming = [];
    const pastList = [];
    for (const ev of groupEvents) {
      const d = (ev.event_date || ev.date || '').slice(0, 10);
      if (d && d < todayStr) pastList.push(ev);
      else upcoming.push(ev);
    }
    // Upcoming asc, past desc (most recent first)
    upcoming.sort((a, b) => (a.event_date || a.date || '').localeCompare(b.event_date || b.date || ''));
    pastList.sort((a, b) => (b.event_date || b.date || '').localeCompare(a.event_date || a.date || ''));
    return { upcomingGroupEvents: upcoming, pastGroupEvents: pastList };
  }, [groupEvents]);
  
  // Member picker state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [availableMembers, setAvailableMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  
  // Transfer ownership dialog
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState(null);
  const [transferring, setTransferring] = useState(false);
  
  // Create event dialog
  const [showCreateEventDialog, setShowCreateEventDialog] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    city: '',
    country: ''
  });
  
  // Edit group dialog
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(false);
  const [editGroupData, setEditGroupData] = useState({
    name: '',
    description: '',
    category: '',
    icon: ''
  });
  const [editIconFile, setEditIconFile] = useState(null);
  const [editIconPreview, setEditIconPreview] = useState(null);
  const editIconInputRef = useRef(null);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  
  // Invite link dialog (external invites)
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [inviteLinkData, setInviteLinkData] = useState(null); // { token, invite_url }
  const [loadingInviteLink, setLoadingInviteLink] = useState(false);
  const [inviteStats, setInviteStats] = useState(null);  // { total, last_7d, last_30d, recent_joiners }
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Manage Admins dialog
  const [showManageAdminsDialog, setShowManageAdminsDialog] = useState(false);
  const [adminActionLoading, setAdminActionLoading] = useState(null); // user_id being acted on
  const [memberFilter, setMemberFilter] = useState('');

  useEffect(() => {
    fetchGroupData();
  }, [slug, user]);

  const fetchGroupData = async () => {
    try {
      const [groupRes, membersRes] = await Promise.all([
        axios.get(`${API}/groups/${slug}`),
        user ? axios.get(`${API}/groups/${slug}/members`).catch(() => ({ data: { members: [] } })) : Promise.resolve({ data: { members: [] } })
      ]);
      
      setGroup(groupRes.data);
      setMembers(membersRes.data.members || []);
      setMembersCountryInfo({
        country: membersRes.data.country || '',
        total_all: membersRes.data.total_all_countries || membersRes.data.total || 0
      });
      
      // Fetch pending invites if admin
      if (groupRes.data.is_admin) {
        try {
          const invitesRes = await axios.get(`${API}/groups/${slug}/pending-invites`);
          setPendingInvites(invitesRes.data.invites || []);
        } catch (e) {
          console.error('Failed to fetch pending invites');
        }
      }
      
      // Fetch group events for any member/admin. Historically this was
      // gated on `is_private` because only private groups had a dedicated
      // events feed, but every group needs a history view so members can
      // browse past events (e.g. "Flight to Brac Croatia July 7").
      if (groupRes.data.is_admin || groupRes.data.is_member) {
        try {
          const eventsRes = await axios.get(`${API}/groups/${slug}/events`);
          setGroupEvents(eventsRes.data || []);
        } catch (e) {
          console.error('Failed to fetch group events');
        }
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You don\'t have access to this private group');
      } else if (err.response?.status === 404) {
        setError('Group not found');
      } else {
        setError('Failed to load group');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/groups/member-cities`);
      setCities(res.data.cities || []);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  const fetchAvailableMembers = async (city, search) => {
    setLoadingMembers(true);
    try {
      const params = new URLSearchParams();
      if (city) params.append('city', city);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/groups/available-members/${slug}?${params}`);
      setAvailableMembers(res.data.members || []);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleOpenMemberPicker = () => {
    setShowInviteDialog(true);
    setSelectedCity('');
    setMemberSearch('');
    setAvailableMembers([]);
    setSelectedMembers(new Set());
    fetchCities();
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setMemberSearch('');
    setSelectedMembers(new Set());
    fetchAvailableMembers(city, '');
  };

  const handleMemberSearch = (search) => {
    setMemberSearch(search);
    fetchAvailableMembers(selectedCity, search);
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleAddMembers = async () => {
    if (selectedMembers.size === 0) {
      toast.error('Please select at least one member');
      return;
    }
    setAddingMembers(true);
    try {
      const res = await axios.post(`${API}/groups/${slug}/add-members`, {
        user_ids: Array.from(selectedMembers)
      });
      toast.success(res.data.message);
      if (res.data.already_members > 0) {
        toast.info(`${res.data.already_members} member(s) were already in the group`);
      }
      setShowInviteDialog(false);
      setSelectedMembers(new Set());
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleCancelInvite = async (inviteId, email) => {
    if (!window.confirm(`Cancel invitation for ${email}?`)) return;
    
    try {
      await axios.delete(`${API}/groups/${slug}/invites/${inviteId}`);
      toast.success('Invitation cancelled');
      fetchGroupData(); // Refresh pending invites
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handleResendInvite = async (inviteId, email) => {
    try {
      await axios.post(`${API}/groups/${slug}/invites/${inviteId}/resend`);
      toast.success(`Invitation resent to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone.`)) return;
    if (!window.confirm('All members will be removed and group data will be permanently deleted. Continue?')) return;
    
    try {
      await axios.delete(`${API}/groups/${slug}`);
      toast.success('Group deleted successfully');
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleMakeAdmin = async (userId, userName) => {
    if (!window.confirm(`Make ${userName} an admin of this group?`)) return;
    
    try {
      await axios.post(`${API}/groups/${slug}/admins`, { user_id: userId });
      toast.success(`${userName} is now an admin`);
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} as admin?`)) return;
    
    try {
      await axios.delete(`${API}/groups/${slug}/admins/${userId}`);
      toast.success(`${userName} is no longer an admin`);
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    
    try {
      await axios.post(`${API}/groups/${slug}/leave`);
      toast.success('Left group');
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave group');
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) {
      toast.error('Please select a member to transfer ownership to');
      return;
    }
    
    setTransferring(true);
    try {
      await axios.post(`${API}/groups/${slug}/transfer-ownership`, { 
        new_owner_id: selectedNewOwner.user_id 
      });
      toast.success(`Ownership transferred to ${selectedNewOwner.name}`);
      setShowTransferDialog(false);
      setSelectedNewOwner(null);
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Please enter a title and date');
      return;
    }
    
    setCreatingEvent(true);
    try {
      await axios.post(`${API}/groups/${slug}/events`, newEvent);
      toast.success('Event created! Members will be notified.');
      setShowCreateEventDialog(false);
      setNewEvent({ title: '', description: '', date: '', time: '', location: '', city: '', country: '' });
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreatingEvent(false);
    }
  };

  const openEditGroupDialog = () => {
    setEditGroupData({
      name: group?.name || '',
      description: group?.description || '',
      category: group?.category || '',
      icon: group?.icon || ''
    });
    setEditIconFile(null);
    setEditIconPreview(null);
    if (editIconInputRef.current) editIconInputRef.current.value = '';
    // Fetch suggestions if not loaded
    if (categorySuggestions.length === 0) {
      axios.get(`${API}/groups/custom-categories`)
        .then(res => setCategorySuggestions(res.data.suggestions || []))
        .catch(() => {});
    }
    setShowEditGroupDialog(true);
  };

  const handleEditIconChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Icon must be under 5MB');
      return;
    }
    setEditIconFile(file);
    setEditIconPreview(URL.createObjectURL(file));
  };

  const handleEditGroup = async () => {
    if (!editGroupData.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    setEditingGroup(true);
    try {
      await axios.put(`${API}/groups/${slug}`, editGroupData);
      
      // Upload new icon if selected
      if (editIconFile) {
        const formData = new FormData();
        formData.append('file', editIconFile);
        try {
          await axios.post(`${API}/groups/${slug}/upload-icon`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (e) {
          toast.warning('Group updated, but icon upload failed.');
        }
      }
      
      toast.success('Group updated successfully');
      setShowEditGroupDialog(false);
      setEditIconFile(null);
      setEditIconPreview(null);
      fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update group');
    } finally {
      setEditingGroup(false);
    }
  };

  // === External invite link (token-based) ===
  const fetchInviteStats = async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/groups/${slug}/invite-stats`);
      setInviteStats(res.data);
    } catch (err) {
      // Non-blocking: stats are optional
      console.error('Failed to fetch invite stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleOpenInviteLink = async () => {
    setShowInviteLinkDialog(true);
    if (!inviteLinkData) {
      setLoadingInviteLink(true);
      try {
        const res = await axios.post(`${API}/groups/${slug}/invite-token`);
        setInviteLinkData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to generate invite link');
        setShowInviteLinkDialog(false);
        setLoadingInviteLink(false);
        return;
      } finally {
        setLoadingInviteLink(false);
      }
    }
    fetchInviteStats();
  };

  const handleCopyInviteLink = () => {
    if (!inviteLinkData?.invite_url) return;
    navigator.clipboard.writeText(inviteLinkData.invite_url).then(() => {
      toast.success('Invite link copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const handleShareInviteLink = async () => {
    if (!inviteLinkData?.invite_url) return;
    const shareData = {
      title: `Join "${group?.name}" on ExpatsSphere`,
      text: `${user?.name || 'A friend'} invited you to join the private group "${group?.name}" on ExpatsSphere.`,
      url: inviteLinkData.invite_url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // user cancelled
      }
    } else {
      handleCopyInviteLink();
    }
  };

  const handleRegenerateInviteLink = async () => {
    if (!window.confirm('Regenerate the invite link? The previous link will stop working.')) return;
    setLoadingInviteLink(true);
    try {
      const res = await axios.delete(`${API}/groups/${slug}/invite-token`);
      setInviteLinkData(res.data);
      toast.success('New invite link generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate link');
    } finally {
      setLoadingInviteLink(false);
    }
  };

  // Manage Admins dialog handlers — wrap existing handlers with inline loading state (no window.confirm)
  const handlePromoteFromDialog = async (member) => {
    setAdminActionLoading(member.user_id);
    try {
      await axios.post(`${API}/groups/${slug}/admins`, { user_id: member.user_id });
      toast.success(`${member.name} is now an admin`);
      await fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote member');
    } finally {
      setAdminActionLoading(null);
    }
  };

  const handleDemoteFromDialog = async (member) => {
    setAdminActionLoading(member.user_id);
    try {
      await axios.delete(`${API}/groups/${slug}/admins/${member.user_id}`);
      toast.success(`${member.name} is no longer an admin`);
      await fetchGroupData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    } finally {
      setAdminActionLoading(null);
    }
  };

  // Check if current user is the owner (creator or first admin)
  const isOwner = group?.creator_id === user?.user_id || group?.admins?.[0] === user?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading group...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-6">{error}</p>
                  <Button onClick={() => navigate('/groups')} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Groups
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Back Button */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/groups')}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Groups
            </Button>

            {/* Group Header */}
            <Card className="border-2 border-border mb-6 overflow-hidden">
              <div className={`p-6 ${group.is_private ? 'bg-gradient-to-br from-purple-600 to-amber-500 text-white' : 'bg-gradient-to-br from-primary to-accent text-white'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {group.icon_url ? (
                      <img src={group.icon_url} alt={group.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30" />
                    ) : (
                      <div className="text-5xl">{group.icon || (group.is_private ? '🔒' : '👥')}</div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h1 className="text-2xl font-bold">{group.name}</h1>
                        {group.is_private && (
                          <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Private
                          </span>
                        )}
                      </div>
                      <p className="text-white/80">{group.description}</p>
                      <p className="text-white/60 text-sm mt-2">
                        <Users className="w-4 h-4 inline mr-1" />
                        {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                        {group.category && ` • ${group.category}`}
                      </p>
                    </div>
                  </div>
                  
                  {group.is_admin && (
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      Admin
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  {group.is_admin && group.is_private && (
                    <Button 
                      onClick={handleOpenMemberPicker}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="add-members-internal-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Members
                    </Button>
                  )}
                  
                  {/* External invite link (shareable) */}
                  {group.is_admin && group.is_private && (
                    <Button
                      onClick={handleOpenInviteLink}
                      variant="outline"
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid="invite-members-external-btn"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Invite Members
                    </Button>
                  )}
                  
                  {/* Edit Group - only for admins of private groups */}
                  {group.is_admin && group.is_private && (
                    <Button 
                      variant="outline"
                      onClick={openEditGroupDialog}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="edit-group-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Group
                    </Button>
                  )}
                  
                  {/* Manage Admins - only for admins of private groups */}
                  {group.is_admin && group.is_private && (
                    <Button
                      variant="outline"
                      onClick={() => { setMemberFilter(''); setShowManageAdminsDialog(true); }}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="manage-admins-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Manage Admins
                    </Button>
                  )}
                  
                  {/* Transfer Ownership - only for owner */}
                  {group.is_private && isOwner && members.length > 1 && (
                    <Button 
                      variant="outline"
                      onClick={() => setShowTransferDialog(true)}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="transfer-ownership-btn"
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Transfer Ownership
                    </Button>
                  )}
                  
                  {(group.is_member || group.is_admin) && (
                    <Button 
                      variant="outline" 
                      onClick={handleLeaveGroup}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      Leave Group
                    </Button>
                  )}
                  {group.is_admin && group.is_private && isOwner && (
                    <Button 
                      variant="outline" 
                      onClick={handleDeleteGroup}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="delete-group-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Group
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Polls — visible to all members */}
            {(group.is_member || group.is_admin) && (
              <GroupPollsSection groupId={group.group_id} groupName={group.name} />
            )}

            {/* Per-group notification location preference */}
            {(group.is_member || group.is_admin) && (
              <GroupNotificationPrefCard slug={group.slug} />
            )}

            {/* Tabs for Admin */}
            {group.is_admin && group.is_private ? (
              <Tabs defaultValue="members" className="space-y-4">
                <TabsList className="bg-white border-2">
                  <TabsTrigger value="members" className="data-[state=active]:bg-purple-100">
                    <Users className="w-4 h-4 mr-2" />
                    Members ({members.length})
                  </TabsTrigger>
                  <TabsTrigger value="events" className="data-[state=active]:bg-purple-100">
                    <Calendar className="w-4 h-4 mr-2" />
                    Events ({groupEvents.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="members">
                  <Card className="border-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Group Members</CardTitle>
                      <CardDescription>People who have joined this private group</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {members.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No members yet. Start inviting people!</p>
                      ) : (
                        <div className="divide-y">
                          {members.map(member => {
                            const isCreatorOrFirstAdmin = group?.admins?.[0] === member.user_id;
                            return (
                            <div key={member.user_id} className="flex items-center justify-between py-3" data-testid={`member-row-${member.user_id}`}>
                              <div className="flex items-center gap-3">
                                {member.profile_picture ? (
                                  <img src={member.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <span className="text-purple-600 font-medium">{member.name?.[0]}</span>
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium flex items-center gap-2">
                                    {member.name}
                                    {member.is_admin && (
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                        {isCreatorOrFirstAdmin ? 'Creator' : 'Admin'}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {member.nationality && `${member.nationality} • `}
                                    {member.residence_city || 'Location not set'}
                                  </p>
                                </div>
                              </div>
                              {/* Admin actions - show for non-self, non-creator members */}
                              {member.user_id !== user?.user_id && !isCreatorOrFirstAdmin && (
                                <div>
                                  {member.is_admin ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs text-muted-foreground hover:text-red-600"
                                      onClick={() => handleRemoveAdmin(member.user_id, member.name)}
                                      data-testid={`remove-admin-${member.user_id}`}
                                    >
                                      Remove Admin
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs text-purple-600 hover:text-purple-700"
                                      onClick={() => handleMakeAdmin(member.user_id, member.name)}
                                      data-testid={`make-admin-${member.user_id}`}
                                    >
                                      <Crown className="w-3 h-3 mr-1" />
                                      Make Admin
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="events">
                  <Card className="border-2">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Group Events</CardTitle>
                        <CardDescription>Events for members of this group</CardDescription>
                      </div>
                      {group.is_admin && (
                        <Button 
                          onClick={() => setShowCreateEventDialog(true)}
                          className="bg-purple-600 hover:bg-purple-700"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Event
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {groupEvents.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No events yet. Create one to bring your group together!</p>
                      ) : (
                        <div className="space-y-6">
                          {upcomingGroupEvents.length > 0 && (
                            <div className="space-y-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="group-events-upcoming-label">
                                Upcoming ({upcomingGroupEvents.length})
                              </p>
                              <div className="space-y-3">
                                {upcomingGroupEvents.map(event => (
                                  <div
                                    key={event.event_id}
                                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                                    onClick={() => navigate(`/events/${event.event_id}`)}
                                    data-testid={`group-event-upcoming-${event.event_id}`}
                                  >
                                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                      <Calendar className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{event.title}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {new Date(event.event_date || event.date).toLocaleDateString()} {event.time && `at ${event.time}`}
                                      </p>
                                      {event.location && (
                                        <p className="text-sm text-muted-foreground truncate">{event.location}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {pastGroupEvents.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="group-events-past-label">
                                  Past ({pastGroupEvents.length})
                                </p>
                              </div>
                              <div className="space-y-3">
                                {pastGroupEvents.map(event => (
                                  <div
                                    key={event.event_id}
                                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer opacity-80"
                                    onClick={() => navigate(`/events/${event.event_id}`)}
                                    data-testid={`group-event-past-${event.event_id}`}
                                  >
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                      <Calendar className="w-6 h-6 text-gray-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{event.title}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {new Date(event.event_date || event.date).toLocaleDateString()} {event.time && `at ${event.time}`}
                                      </p>
                                      {event.location && (
                                        <p className="text-sm text-muted-foreground truncate">{event.location}</p>
                                      )}
                                    </div>
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                      Past
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            ) : (
              /* Non-admin members view */
              <>
                {/* Group Events history — visible to all members. Splits into
                    Upcoming vs Past so members can browse events they missed
                    (or didn't RSVP to) even after the event date passes. */}
                {(group.is_member || group.is_admin) && groupEvents.length > 0 && (
                  <Card className="border-2 mb-6" data-testid="group-events-member-card">
                    <CardHeader>
                      <CardTitle className="text-lg">Group Events</CardTitle>
                      <CardDescription>Upcoming and past events for this group</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {upcomingGroupEvents.length > 0 && (
                          <div className="space-y-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="group-events-upcoming-label">
                              Upcoming ({upcomingGroupEvents.length})
                            </p>
                            <div className="space-y-3">
                              {upcomingGroupEvents.map(event => (
                                <div
                                  key={event.event_id}
                                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                                  onClick={() => navigate(`/events/${event.event_id}`)}
                                  data-testid={`group-event-upcoming-${event.event_id}`}
                                >
                                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-6 h-6 text-purple-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{event.title}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {new Date(event.event_date || event.date).toLocaleDateString()} {event.time && `at ${event.time}`}
                                    </p>
                                    {event.location && (
                                      <p className="text-sm text-muted-foreground truncate">{event.location}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {pastGroupEvents.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="group-events-past-label">
                                Past ({pastGroupEvents.length})
                              </p>
                              {pastGroupEvents.length > 3 && (
                                <button
                                  type="button"
                                  onClick={() => setShowPastGroupEvents(v => !v)}
                                  className="text-xs text-purple-600 hover:underline"
                                  data-testid="toggle-past-group-events"
                                >
                                  {showPastGroupEvents ? 'Show fewer' : `Show all (${pastGroupEvents.length})`}
                                </button>
                              )}
                            </div>
                            <div className="space-y-3">
                              {(showPastGroupEvents ? pastGroupEvents : pastGroupEvents.slice(0, 3)).map(event => (
                                <div
                                  key={event.event_id}
                                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer opacity-80"
                                  onClick={() => navigate(`/events/${event.event_id}`)}
                                  data-testid={`group-event-past-${event.event_id}`}
                                >
                                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-6 h-6 text-gray-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{event.title}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {new Date(event.event_date || event.date).toLocaleDateString()} {event.time && `at ${event.time}`}
                                    </p>
                                    {event.location && (
                                      <p className="text-sm text-muted-foreground truncate">{event.location}</p>
                                    )}
                                  </div>
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                    Past
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Group Members
                    {membersCountryInfo.country && membersCountryInfo.country !== 'All' && (
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        in {membersCountryInfo.country} ({members.length} of {membersCountryInfo.total_all} total)
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {members.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      {membersCountryInfo.country && membersCountryInfo.country !== 'All'
                        ? `No members in ${membersCountryInfo.country} yet (${membersCountryInfo.total_all} total across all countries)`
                        : 'No members yet'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {members.map(member => {
                        const isAdmin = group?.admins?.includes(member.user_id);
                        const isCurrentUserAdmin = group?.admins?.includes(user?.user_id);
                        const isCreator = group?.creator_id === member.user_id || group?.admins?.[0] === member.user_id;
                        
                        return (
                        <div key={member.user_id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {member.profile_picture ? (
                              <img src={member.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-600 font-medium">{member.name?.[0]}</span>
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{member.name}</p>
                                {isAdmin && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                    {isCreator ? 'Creator' : 'Admin'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{member.nationality ? `${member.nationality} • ` : ''}{member.residence_city || 'Location not set'}</p>
                            </div>
                          </div>
                          {isCurrentUserAdmin && member.user_id !== user?.user_id && !isCreator && (
                            <div>
                              {isAdmin ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs text-muted-foreground hover:text-red-600"
                                  onClick={() => handleRemoveAdmin(member.user_id, member.name)}
                                >
                                  Remove Admin
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs text-purple-600 hover:text-purple-700"
                                  onClick={() => handleMakeAdmin(member.user_id, member.name)}
                                >
                                  <Crown className="w-3 h-3 mr-1" />
                                  Make Admin
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              </>
            )}
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Add Members to "{group?.name}"
            </DialogTitle>
            <DialogDescription>
              Select a city to browse members, then check the ones you want to add
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-3 flex-1 overflow-hidden">
            {/* City selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select City</Label>
              <div className="relative">
                <Input
                  placeholder="Search cities..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="h-10"
                  data-testid="city-search-input"
                />
              </div>
              {!selectedCity && (
                <div className="max-h-[140px] overflow-y-auto border rounded-lg divide-y">
                  {cities
                    .filter(c => !citySearch || c.city.toLowerCase().includes(citySearch.toLowerCase()))
                    .map(c => (
                      <button
                        key={c.city}
                        onClick={() => handleCitySelect(c.city)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-50 text-left text-sm transition-colors"
                        data-testid={`city-option-${c.city}`}
                      >
                        <span className="font-medium">{c.city}</span>
                        <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">{c.member_count} member{c.member_count !== 1 ? 's' : ''}</span>
                      </button>
                    ))}
                  {cities.filter(c => !citySearch || c.city.toLowerCase().includes(citySearch.toLowerCase())).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-3">No cities found</p>
                  )}
                </div>
              )}
              {selectedCity && (
                <div className="flex items-center gap-2">
                  <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">{selectedCity}</span>
                  <button onClick={() => { setSelectedCity(''); setAvailableMembers([]); setSelectedMembers(new Set()); }} className="text-xs text-muted-foreground hover:text-red-500">Change</button>
                </div>
              )}
            </div>

            {/* Member list */}
            {selectedCity && (
              <>
                <div>
                  <Input
                    placeholder="Search members by name..."
                    value={memberSearch}
                    onChange={(e) => handleMemberSearch(e.target.value)}
                    className="h-10"
                    data-testid="member-search-input"
                  />
                </div>
                
                {selectedMembers.size > 0 && (
                  <p className="text-sm text-purple-600 font-medium">{selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected</p>
                )}

                <div className="flex-1 overflow-y-auto border rounded-lg divide-y min-h-0 max-h-[280px]">
                  {loadingMembers ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-5 h-5 animate-spin text-purple-500" />
                    </div>
                  ) : availableMembers.length > 0 ? (
                    availableMembers.map(m => (
                      <label
                        key={m.user_id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${selectedMembers.has(m.user_id) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                        data-testid={`member-option-${m.user_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(m.user_id)}
                          onChange={() => toggleMember(m.user_id)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        {m.profile_picture ? (
                          <img src={m.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-bold">
                            {m.name?.[0] || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{m.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {[m.residence_city, m.nationality].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No available members found in {selectedCity}</p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddMembers}
              disabled={addingMembers || selectedMembers.size === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-members-btn"
            >
              {addingMembers ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add {selectedMembers.size > 0 ? `${selectedMembers.size} ` : ''}Member{selectedMembers.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              Transfer Ownership
            </DialogTitle>
            <DialogDescription>
              Select a member to become the new owner of "{group?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
              <strong>Warning:</strong> This action cannot be undone. The new owner will have full control of the group, including the ability to delete it.
            </p>
            
            <Label className="mb-3 block">Select New Owner</Label>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {members
                .filter(m => m.user_id !== user?.user_id)
                .map(member => (
                  <div 
                    key={member.user_id}
                    onClick={() => setSelectedNewOwner(member)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-all ${
                      selectedNewOwner?.user_id === member.user_id 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                    data-testid={`select-owner-${member.user_id}`}
                  >
                    {member.profile_picture ? (
                      <img src={member.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-medium">{member.name?.[0]}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {member.nationality && `${member.nationality} • `}
                        {member.residence_city || 'Location not set'}
                      </p>
                    </div>
                    {selectedNewOwner?.user_id === member.user_id && (
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                ))}
            </div>
            
            {members.filter(m => m.user_id !== user?.user_id).length === 0 && (
              <p className="text-muted-foreground text-center py-4">
                No other members to transfer ownership to.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTransferDialog(false);
                setSelectedNewOwner(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTransferOwnership}
              disabled={transferring || !selectedNewOwner}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-transfer-btn"
            >
              {transferring ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Transferring...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer Ownership
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showCreateEventDialog} onOpenChange={setShowCreateEventDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Create Group Event
            </DialogTitle>
            <DialogDescription>
              Create an event for members of "{group?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="event-title">Event Title *</Label>
              <Input
                id="event-title"
                value={newEvent.title}
                onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Monthly Meetup"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="event-description">Description</Label>
              <Textarea
                id="event-description"
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's this event about?"
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-date">Date *</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="event-time">Time</Label>
                <Input
                  id="event-time"
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="event-location">Location</Label>
              <Input
                id="event-location"
                value={newEvent.location}
                onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Coffee House, 123 Main St"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-city">City</Label>
                <Input
                  id="event-city"
                  value={newEvent.city}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Budapest"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="event-country">Country</Label>
                <Input
                  id="event-country"
                  value={newEvent.country}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Hungary"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowCreateEventDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEvent}
              disabled={creatingEvent || !newEvent.title || !newEvent.date}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingEvent ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={showEditGroupDialog} onOpenChange={setShowEditGroupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-600" />
              Edit Group
            </DialogTitle>
            <DialogDescription>
              Update your group's name and description
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-group-name">Group Name</Label>
              <Input
                id="edit-group-name"
                value={editGroupData.name}
                onChange={(e) => setEditGroupData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter group name"
                className="mt-1"
                data-testid="edit-group-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-group-description">Description</Label>
              <Textarea
                id="edit-group-description"
                value={editGroupData.description}
                onChange={(e) => setEditGroupData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your group..."
                className="mt-1"
                rows={4}
                data-testid="edit-group-description-input"
              />
            </div>

            {/* Icon: emoji + image upload */}
            <div className="space-y-2">
              <Label>Group Icon / Logo</Label>
              <div className="flex items-center gap-3">
                {editIconPreview ? (
                  <img src={editIconPreview} alt="Icon preview" className="w-16 h-16 rounded-xl object-cover border-2 border-purple-200" />
                ) : group?.icon_url ? (
                  <img src={group.icon_url} alt="Current icon" className="w-16 h-16 rounded-xl object-cover border-2 border-purple-200" />
                ) : (
                  <Input
                    placeholder="🔒"
                    value={editGroupData.icon}
                    onChange={(e) => setEditGroupData(prev => ({ ...prev, icon: e.target.value }))}
                    className="text-2xl text-center w-16 h-16"
                    data-testid="edit-group-emoji-input"
                  />
                )}
                <div className="flex-1">
                  <input
                    ref={editIconInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleEditIconChange}
                    className="hidden"
                    data-testid="edit-group-icon-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editIconInputRef.current?.click()}
                    data-testid="edit-group-upload-icon-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {editIconPreview || group?.icon_url ? 'Change image' : 'Upload image'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">Max 5MB</p>
                </div>
              </div>
            </div>

            {/* Category: free-text + suggestions */}
            <div>
              <Label htmlFor="edit-group-category">Category</Label>
              <Input
                id="edit-group-category"
                list="edit-group-category-suggestions"
                value={editGroupData.category}
                onChange={(e) => setEditGroupData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Type a category"
                className="mt-1"
                data-testid="edit-group-category-input"
              />
              <datalist id="edit-group-category-suggestions">
                {PRESET_CATEGORIES.map(cat => (
                  <option key={`preset-${cat}`} value={cat} />
                ))}
                {categorySuggestions.filter(s => !PRESET_CATEGORIES.includes(s)).map(s => (
                  <option key={`custom-${s}`} value={s} />
                ))}
              </datalist>
              {editGroupData.category && !PRESET_CATEGORIES.includes(editGroupData.category.trim()) && editGroupData.category.trim() && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" />
                  Custom category will be reviewed by an admin.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowEditGroupDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditGroup}
              disabled={editingGroup || !editGroupData.name.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {editingGroup ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Link Dialog (external shareable invite) */}
      <Dialog open={showInviteLinkDialog} onOpenChange={setShowInviteLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-600" />
              Invite People to "{group?.name}"
            </DialogTitle>
            <DialogDescription>
              Share this link with friends. People who don't have an account will be auto-joined after signing up.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {loadingInviteLink && !inviteLinkData ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-purple-500" />
              </div>
            ) : inviteLinkData ? (
              <>
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <Input
                    readOnly
                    value={inviteLinkData.invite_url}
                    className="bg-white text-sm font-mono"
                    onClick={(e) => e.target.select()}
                    data-testid="invite-link-url-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCopyInviteLink}
                    className="bg-purple-600 hover:bg-purple-700 shrink-0"
                    data-testid="copy-invite-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleShareInviteLink}
                    variant="outline"
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    data-testid="share-invite-link-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button
                    onClick={handleRegenerateInviteLink}
                    variant="ghost"
                    className="text-muted-foreground hover:text-red-600"
                    disabled={loadingInviteLink}
                    data-testid="regenerate-invite-link-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingInviteLink ? 'animate-spin' : ''}`} />
                    Regenerate
                  </Button>
                </div>
                
                <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-1">
                  <p className="font-medium text-amber-700">How it works</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Anyone with this link can join the group</li>
                    <li>New users sign up and are automatically added</li>
                    <li>This link never expires unless you regenerate it</li>
                  </ul>
                </div>
                
                {/* Invite Analytics — non-blocking, only renders when data is available */}
                <div className="border-t pt-4 space-y-3" data-testid="invite-analytics-section">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-purple-600" />
                      Invite link analytics
                    </p>
                    {loadingStats && <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-400" />}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg border bg-white p-3 text-center">
                      <p className="text-2xl font-bold text-purple-600" data-testid="invite-stats-total">{inviteStats?.total ?? '—'}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">All time</p>
                    </div>
                    <div className="rounded-lg border bg-white p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600" data-testid="invite-stats-30d">{inviteStats?.last_30d ?? '—'}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">Last 30 days</p>
                    </div>
                    <div className="rounded-lg border bg-white p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600" data-testid="invite-stats-7d">{inviteStats?.last_7d ?? '—'}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">Last 7 days</p>
                    </div>
                  </div>
                  
                  {inviteStats?.recent_joiners?.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Recent joiners</p>
                      <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                        {inviteStats.recent_joiners.map(j => (
                          <div key={`${j.user_id}-${j.joined_at}`} className="flex items-center gap-2.5 text-sm" data-testid={`recent-joiner-${j.user_id}`}>
                            {j.profile_picture ? (
                              <img src={j.profile_picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold">
                                {j.name?.[0] || '?'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-xs truncate">{j.name}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {j.source === 'signup' ? 'New signup' : 'Existing user'} · {new Date(j.joined_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {inviteStats && inviteStats.total === 0 && !loadingStats && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      No one has joined via this link yet. Share it to grow your group!
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowInviteLinkDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Admins Dialog */}
      <Dialog open={showManageAdminsDialog} onOpenChange={setShowManageAdminsDialog}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-purple-600" />
              Manage Admins
            </DialogTitle>
            <DialogDescription>
              Promote members to admin or remove existing admins. The group creator is protected.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 py-2">
            {/* Current Admins */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5" />
                Current admins ({members.filter(m => group?.admins?.includes(m.user_id)).length})
              </p>
              <div className="divide-y border rounded-lg">
                {members
                  .filter(m => group?.admins?.includes(m.user_id))
                  .map(member => {
                    const isCreator = group?.creator_id === member.user_id || group?.admins?.[0] === member.user_id;
                    const isMe = member.user_id === user?.user_id;
                    const isLoading = adminActionLoading === member.user_id;
                    return (
                      <div key={member.user_id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`admin-row-${member.user_id}`}>
                        {member.profile_picture ? (
                          <img src={member.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-sm font-bold">
                            {member.name?.[0] || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate flex items-center gap-1.5">
                            {member.name}
                            {isMe && <span className="text-[10px] text-muted-foreground font-normal">(you)</span>}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {isCreator ? 'Creator' : 'Admin'}
                            {member.residence_city && ` · ${member.residence_city}`}
                          </p>
                        </div>
                        {isCreator ? (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold flex items-center gap-1 shrink-0">
                            <Crown className="w-3 h-3" />
                            Creator
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isLoading}
                            onClick={() => handleDemoteFromDialog(member)}
                            className="text-xs text-muted-foreground hover:text-red-600 shrink-0"
                            data-testid={`demote-admin-${member.user_id}`}
                          >
                            {isLoading ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>Remove</>
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            
            {/* Promote a Member */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Promote a member
              </p>
              <Input
                placeholder="Search members by name..."
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                className="h-9"
                data-testid="promote-member-search"
              />
              <div className="divide-y border rounded-lg max-h-[260px] overflow-y-auto">
                {(() => {
                  const candidates = members.filter(m =>
                    !group?.admins?.includes(m.user_id) &&
                    (!memberFilter || m.name?.toLowerCase().includes(memberFilter.toLowerCase()))
                  );
                  if (candidates.length === 0) {
                    return (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        {memberFilter ? 'No matching members' : 'All members are already admins'}
                      </p>
                    );
                  }
                  return candidates.map(member => {
                    const isLoading = adminActionLoading === member.user_id;
                    return (
                      <div key={member.user_id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`promote-row-${member.user_id}`}>
                        {member.profile_picture ? (
                          <img src={member.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-sm font-bold">
                            {member.name?.[0] || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{member.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {[member.nationality, member.residence_city].filter(Boolean).join(' · ') || 'Member'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          disabled={isLoading}
                          onClick={() => handlePromoteFromDialog(member)}
                          className="bg-purple-600 hover:bg-purple-700 text-xs h-7 px-3 shrink-0"
                          data-testid={`promote-${member.user_id}`}
                        >
                          {isLoading ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <Crown className="w-3 h-3 mr-1" />
                              Promote
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowManageAdminsDialog(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default GroupDetail;

// ---------------------------------------------------------------------------
// Group-scoped polls section. Lives inside GroupDetail so it shares the same
// imports/auth context. Renders the polls list + a "Start a Poll" button.
// ---------------------------------------------------------------------------
function GroupPollsSection({ groupId, groupName }) {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchPolls = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/polls/group/${groupId}`);
      setPolls(res.data?.polls || []);
    } catch {
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupId) fetchPolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  return (
    <>
      <Card className="border-2" data-testid="group-polls-section">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-600" />
            <CardTitle className="text-lg">Polls</CardTitle>
          </div>
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="rounded-full bg-violet-600 hover:bg-violet-700"
            data-testid="group-polls-create-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Poll
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && (
            <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>
          )}
          {!loading && polls.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No polls yet. Start one to see if members are interested in an activity.
            </p>
          )}
          {!loading && polls.map((p) => (
            <PollCard
              key={p.poll_id}
              poll={p}
              compact
              onUpdated={(u) => setPolls((prev) => prev.map((x) => (x.poll_id === u.poll_id ? u : x)))}
              onRemoved={(id) => setPolls((prev) => prev.filter((x) => x.poll_id !== id))}
            />
          ))}
        </CardContent>
      </Card>

      <CreatePollDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        defaultScope="group"
        defaultGroupId={groupId}
        onCreated={() => fetchPolls()}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// GroupNotificationPrefCard: lets a member of this group choose whether
// they want event-notifications for *all* events in the group worldwide,
// or only events in their own city (default).
// ---------------------------------------------------------------------------
function GroupNotificationPrefCard({ slug }) {
  const [pref, setPref] = useState('city');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/groups/${slug}/notification-preference`).then((r) => {
      setPref(r.data?.preference || 'city');
    }).catch(() => {
      /* default 'city' is fine */
    }).finally(() => setLoading(false));
  }, [slug]);

  const update = async (next) => {
    if (next === pref) return;
    setSaving(true);
    const prev = pref;
    setPref(next);  // optimistic
    try {
      await axios.put(`${API}/groups/${slug}/notification-preference`, { preference: next });
      toast.success(next === 'all' ? 'Now notifying you about events worldwide' : 'Now only notifying you about local events');
    } catch (e) {
      setPref(prev);
      toast.error('Could not update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-2" data-testid="group-notif-pref-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Event notifications</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => update('city')}
              disabled={saving}
              className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${
                pref === 'city'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="group-pref-city"
            >
              <p className="font-semibold text-sm">My city only</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Default. Get notified only about events in your city.
              </p>
            </button>
            <button
              type="button"
              onClick={() => update('all')}
              disabled={saving}
              className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${
                pref === 'all'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="group-pref-all"
            >
              <p className="font-semibold text-sm">All events worldwide</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Get notified about every event this group hosts, anywhere.
              </p>
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

